import { Clock } from "lucide-react"
import { formatDateTime } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type ClosedNoticeProps = {
  /** ISO instant the restaurant next opens, or null when there is none to promise. */
  opensAt: string | null
  /**
   * The restaurant's zone. Every diner-facing hour renders in it, never the
   * browser's — so this is nullable and the opening line is withheld without it,
   * rather than quietly falling back to whatever zone the phone happens to be in.
   */
  timeZone: string | null
  className?: string
}

/**
 * States that ordering is off right now, on a screen that stays readable.
 *
 * The menu is worth reading when the kitchen is shut — someone deciding where to
 * eat tomorrow is a diner too, and the public menu is opt-in, so a pickup-only
 * restaurant would otherwise go dark every night. What closes is the ordering.
 *
 * The opening hour is the substance. "Closed" on its own cannot tell a
 * ten-minute wait from a wasted trip, so when the schedule can name the next
 * opening this says it, in the restaurant's zone. When it cannot — no hours
 * configured — the line is simply absent rather than guessed at.
 *
 * `--warning` rather than a destructive tone: nothing has gone wrong, and the
 * restaurant is not unavailable. It is closed, and it opens again.
 */
export function ClosedNotice({ opensAt, timeZone, className }: ClosedNoticeProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-xl border border-warning/30 bg-warning/8 px-4 py-3",
        className
      )}
      role="status"
      data-testid="pickup-closed-notice"
    >
      <Clock className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-foreground">
          {t("ui.react.features.user.pickup_closed.title")}
        </p>
        {opensAt && timeZone && (
          <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
            {t("ui.react.features.user.pickup_closed.opens_at", {
              date: formatDateTime(opensAt, { timeZone }),
            })}
          </p>
        )}
      </div>
    </div>
  )
}
